import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { LanguageService } from 'src/app/language/language.service';
import { MappingsService } from 'src/app/nav-processing/mappings/mappings.service';
import { MappingsOutput, MappingsOutputType } from 'src/app/nav-processing/mappings/mappings.model';
import { GazeWebcamService } from 'src/app/nav-driver/gaze-webcam/gaze-webcam.service';
import { ProfileService } from 'src/app/profile/profile.service';
import { DwellTimeService } from 'src/app/link/services/dwell-time.service';



/**
 * GazeCursorService
 *
 * Cursor driven by a gaze model that outputs normalized coordinates in [-1, 1].
 * - Maps to screen pixels and finds hovered navigable element ([data-grid-col]).
 * - Implements independent per-element dwell timers with leak for non-hovered elements.
 *
 * Speed Profile System:
 * All speed-related parameters (dwell time, leak rate, stickiness, transition)
 * are derived from a single dwell time value via updateSpeedProfile().
 * The modal slider controls everything through one unified setting.
 */
@Injectable({ providedIn: 'root' })
export class GazeCursorService {
  private sub: Subscription | null = null;
  private dwellSub: Subscription | null = null;

  private lastTs = 0;

  private currentElm: Element | null = null;
  private dwellMsByElm = new Map<Element, number>();

  private gaze01Subject = new BehaviorSubject<{ x01: number; y01: number; valid: boolean }>({
  x01: 0.5,
  y01: 0.5,
  valid: false,
});
  public gaze01$ = this.gaze01Subject.asObservable();


  // Visual feedback elements for the ring animation
  private dwellElm: HTMLDivElement | null = null; // container
  private ringElm: HTMLDivElement | null = null; // the animating ring
  private gazeDotElm: HTMLDivElement | null = null; // gaze point indicator
  private dwellHideTimer: number | null = null; // deferred hide handle

  // Internal state for gaze coordinates in [0,1]
  private gaze01 = { x: 0.5, y: 0.5 };
  // We only start dwell/highlight once a valid gaze point has been received
  private hasValidGaze = false;
  private lastGazeTimestamp = 0; // timestamp of last valid gaze point
  private readonly GAZE_TIMEOUT_MS = 3000; // invalidate gaze if no points for 3s
  private viewGazePoint = true; // added for debug
  // If true, hide the ring/border that outlines the dwell overlay
  private hideDwellBorder = false;

  // Prevent immediate re-activation until gaze leaves the element
  private disarmedUntilExit = new Set<Element>();
  private lastHoverElm: Element | null = null;
  // Debug: use mouse pointer as gaze source
  private mouseDebugActive = false;
  private mouseMoveHandler = (e: MouseEvent) => {
    // Normalize to [0,1] using viewport dimensions
    this.gaze01.x = Math.min(1, Math.max(0, e.clientX / window.innerWidth));
    this.gaze01.y = Math.min(1, Math.max(0, e.clientY / window.innerHeight));
    // Treat mouse move as a valid gaze input for debug
    this.hasValidGaze = true;
    this.gaze01Subject.next({ x01: this.gaze01.x, y01: this.gaze01.y, valid: true });

  };
  private rafId: number | null = null;

  // Pause/Resume state
  private isPaused = false;
  private toolbarElement: Element | null = null;
  private hasNavigableElementsOnPage = false;
  private resumeTargets: HTMLDivElement[] = [];
  private resumeSequenceIndex = 0;
  private resumeTargetTimeout: number | null = null;
  private readonly RESUME_SEQUENCE_LENGTH = 2;
  private readonly RESUME_TARGET_TIMEOUT_MS = 2000; // 2 seconds per target

  /** Single source of truth for dwell duration at runtime.
   *  Loaded from DwellTimeService on start(), kept in sync via dwellSub. */
  private runtimeDwellMs: number = 1500;

  // ── Speed Profile: derived parameters ──────────────────────────────
  // All computed from dwell time via applySpeedProfileFromDwell().
  // leakRate: fraction of dwellTime drained per second from non-hovered elements
  private runtimeLeakRate: number = 0.15;
  // stickinessPx: hysteresis radius (px) to keep current element before switching
  private runtimeStickinessPx: number = 30;
  // transitionMs: CSS transition duration for dwell overlay movement
  private runtimeTransitionMs: number = 220;
  // ───────────────────────────────────────────────────────────────────


  // User-requested pause (toggle) handling
  private userPauseToggleElm: Element | null = null;
  private userPauseActive = false;
  private pauseOverlayElm: HTMLDivElement | null = null;
  private pauseOverlayMessageElm: HTMLDivElement | null = null;

  private detectMovedMessage:  HTMLDivElement | null = null;
  private detectMovedMessageElm: HTMLDivElement | null = null;

  constructor(
    private mappings: MappingsService,
    private profile: ProfileService,
    private lang: LanguageService,
    private gazeWebcam: GazeWebcamService, // --- IGNORE ---
    private dwell: DwellTimeService
  ) { }

  // ── Speed Profile helpers ──────────────────────────────────────────

  /**
   * Derive all speed-related parameters from a dwell time value.
   * t = 0 → fast (500 ms), t = 1 → slow (3000 ms).
   *
   * | Parameter     | Fast (500 ms) | Slow (3000 ms) | Effect                           |
   * |---------------|---------------|----------------|----------------------------------|
   * | leakRate      | 0.35          | 0.06           | How fast a letter "forgets" gaze  |
   * | stickinessPx  | 16 px         | 48 px          | Hysteresis radius on element      |
   * | transitionMs  | 120 ms        | 350 ms         | Visual smoothness of cursor move  |
   */
  private applySpeedProfileFromDwell(dwellMs: number): void {
    const t = Math.max(0, Math.min(1, (dwellMs - 500) / 2500));

    this.runtimeLeakRate      = 0.35 - t * 0.29;   // 0.35 → 0.06
    this.runtimeStickinessPx  = 16   + t * 32;      // 16   → 48
    this.runtimeTransitionMs  = 120  + t * 230;     // 120  → 350

    this.applyTransitionStyles();
  }

  /** Push the current transitionMs into the dwell/ring overlay CSS. */
  private applyTransitionStyles(): void {
    const ease = 'cubic-bezier(0.2,0.8,0.2,1)';
    const ms = Math.round(this.runtimeTransitionMs);

    if (this.dwellElm) {
      this.dwellElm.style.transition =
        `top ${ms}ms ${ease}, left ${ms}ms ${ease}, width ${ms}ms ${ease}, height ${ms}ms ${ease}, opacity 140ms linear`;
    }
    if (this.ringElm) {
      this.ringElm.style.transition =
        `top ${ms}ms ${ease}, left ${ms}ms ${ease}, width ${ms}ms ${ease}, height ${ms}ms ${ease}, border-radius ${ms}ms ${ease}, opacity 140ms linear`;
    }
  }

  // ────────────────────────────────────────────────────────────────────

  private clearHighlights(except?: Element | null) {
    const nodes = document.querySelectorAll('.app-cursor-highlight');
    nodes.forEach((n) => {
      if (!except || n !== except) {
        n.classList.remove('app-cursor-highlight');
      }
    });
  }

  async start(opts?: { forceMouse?: boolean; forceViewPoint?: boolean }) {
if (this.sub) { this.sub.unsubscribe(); this.sub = null; }
if (this.rafId) { cancelAnimationFrame(this.rafId); this.rafId = null; }
  await this.dwell.init();
  this.runtimeDwellMs = this.dwell.current;

  // Derive speed profile from persisted dwell time
  this.applySpeedProfileFromDwell(this.runtimeDwellMs);

  this.dwellSub?.unsubscribe();
  this.dwellSub = this.dwell.ms$.subscribe(ms => {
    this.runtimeDwellMs = ms;
    // Keep derived params in sync when dwell changes externally
    this.applySpeedProfileFromDwell(ms);
  });

  this.hideDwellBorder =
    this.profile.navSettingsBundle?.bundleOptionsState?.gazeHideDwellBorder ?? true;

  this.createDwellElm();
  this.refreshActivePageContext();
  this.clearHighlights();

  this.lastTs = performance.now();
  this.hasValidGaze = false;
  this.lastGazeTimestamp = 0;

  const profileMouse = !!this.profile.navSettingsBundle?.bundleOptionsState?.gazeDebugUseMouse;
  this.mouseDebugActive = opts?.forceMouse ?? profileMouse;

  const profileView = this.profile.navSettingsBundle?.bundleOptionsState?.gazePointVisualization ?? true;
  this.viewGazePoint = opts?.forceViewPoint ?? profileView;

  console.log('GazeCursorService: mouseDebugActive=', this.mouseDebugActive, 'viewGazePoint=', this.viewGazePoint);

  this.userPauseActive = false;
  this.gaze01Subject.next({ x01: this.gaze01.x, y01: this.gaze01.y, valid: false });

  if (this.mouseDebugActive) {
    window.addEventListener('mousemove', this.mouseMoveHandler, { passive: true });
    this.startAnimationLoop();
    return;
  }

  await this.gazeWebcam.start();
  this.sub = this.gazeWebcam.gazePoint$.subscribe((ev) => this.onGaze(ev));
  this.startAnimationLoop();
}

  public refreshActivePageContext(): void {
    const previousToolbar = this.toolbarElement;
    const hadNavigableElements = this.hasNavigableElementsOnPage;

    const ionPages = Array.from(document.querySelectorAll('.ion-page'));
    const activePage = ionPages.length > 0 ? ionPages[ionPages.length - 1] : null;
    const scope = activePage ?? document;

    const navigableItems = scope.querySelectorAll('[data-grid-col]');
    const hasNavigableElements = navigableItems.length > 0;

    let nextToolbar: Element | null = null;
    if (hasNavigableElements) {
      nextToolbar = activePage?.querySelector('ion-toolbar') ?? document.querySelector('ion-toolbar');
    }

    if (previousToolbar && previousToolbar !== nextToolbar) {
      previousToolbar.classList.remove('gaze-toolbar-large');
      (previousToolbar as HTMLElement).style.zIndex = '';
    }

    this.hasNavigableElementsOnPage = hasNavigableElements;

    if (hasNavigableElements && nextToolbar) {
      nextToolbar.classList.add('gaze-toolbar-large');
      (nextToolbar as HTMLElement).style.zIndex = '100';
      this.toolbarElement = nextToolbar;
    } else {
      if (nextToolbar) {
        nextToolbar.classList.remove('gaze-toolbar-large');
        (nextToolbar as HTMLElement).style.zIndex = '';
      }
      this.toolbarElement = null;
    }

    if (!hasNavigableElements) {
      this.clearHighlights();
      this.dwellMsByElm.clear();
      this.currentElm = null;
      this.lastHoverElm = null;
      this.disarmedUntilExit.clear();
      this.scheduleDwellHide(true);
      this.hidePauseOverlay();
      this.resumeTargets.forEach((target) => target.remove());
      this.resumeTargets = [];
      this.clearResumeTargetTimeout();
      if (this.gazeDotElm) {
        this.gazeDotElm.style.display = 'none';
      }
    } else if (!hadNavigableElements && this.isPaused) {
      this.startResumeSequence();
      this.showPauseOverlay(this.getPauseOverlayMessage());
    }
  }

  stop() {
    this.sub?.unsubscribe();
    this.sub = null;

    this.dwellSub?.unsubscribe();
    this.dwellSub = null;

    this.gaze01Subject.next({ x01: this.gaze01.x, y01: this.gaze01.y, valid: false });

    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.mouseDebugActive) {
      window.removeEventListener('mousemove', this.mouseMoveHandler as any);
      this.mouseDebugActive = false;
    }
    this.hasValidGaze = false;
    this.isPaused = false;
    this.userPauseActive = false;
    this.userPauseToggleElm = null;
    this.hasNavigableElementsOnPage = false;

    // Remove toolbar styling
    if (this.toolbarElement) {
      this.toolbarElement.classList.remove('gaze-toolbar-large');
      (this.toolbarElement as HTMLElement).style.zIndex = '';
    }

    this.teardownVisuals();
    this.dwellMsByElm.clear();
    this.currentElm = null;
    this.lastHoverElm = null;
    this.disarmedUntilExit.clear();
    if (this.dwellHideTimer !== null) {
      clearTimeout(this.dwellHideTimer);
      this.dwellHideTimer = null;
    }
    // Clean up resume targets
    this.resumeTargets.forEach(target => target.remove());
    this.resumeTargets = [];
    this.clearResumeTargetTimeout();
    // Stop the gaze webcam service
    //this.gazeWebcam.stop();
    this.hidePauseOverlay();
    if (this.pauseOverlayElm) {
      this.pauseOverlayElm.remove();
      this.pauseOverlayElm = null;
      this.pauseOverlayMessageElm = null;
    }
    this.hideCalibrationOverlay();
    if (this.detectMovedMessage) {
      this.detectMovedMessage.remove();
      this.detectMovedMessage = null;
      this.detectMovedMessageElm = null;
    }
      this.lastGazeTimestamp = 0;
  }

  setUserPauseToggleElement(elm: Element | null) {
    this.userPauseToggleElm = elm ?? null;
  }

  toggleUserPause(toggleElm?: Element | null): boolean {
    if (toggleElm) {
      this.userPauseToggleElm = toggleElm;
    }

    if (!this.userPauseToggleElm) {
      this.userPauseActive = false;
      return this.userPauseActive;
    }

    if (this.userPauseActive) {
      this.userPauseActive = false;
      this.resumeGazeSystem();
      return this.userPauseActive;
    }

    this.userPauseActive = true;
    this.pauseGazeSystem();
    return this.userPauseActive;
  }

  /**
   * Pause the gaze cursor system without showing resume sequence UI.
   * Use this when you need to temporarily disable gaze control programmatically.
   */
  pause() {
    if (this.isPaused) return;
    this.isPaused = true;
    
    // Clear all dwell timers and highlights
    this.dwellMsByElm.clear();
    this.clearHighlights();
    this.currentElm = null;
    this.lastHoverElm = null;
    this.disarmedUntilExit.clear();
    
    // Hide dwell visuals
    if (this.dwellElm) {
      this.dwellElm.style.opacity = '0';
    }
    if (this.ringElm) {
      this.ringElm.style.opacity = '0';
    }
    if (this.gazeDotElm) {
      this.gazeDotElm.style.display = 'none';
    }
  }

  /**
   * Resume the gaze cursor system without requiring resume sequence.
   * Use this to restore gaze control after calling pause().
   */
  resume() {
    if (!this.isPaused) return;
    this.isPaused = false;
    this.userPauseActive = false;
    
    // Clean up any resume sequence elements if they exist
    this.resumeTargets.forEach(target => target.remove());
    this.resumeTargets = [];
    this.clearResumeTargetTimeout();
    
    // Hide pause overlay if visible
    this.hidePauseOverlay();
  }



  isUserPauseActive(): boolean {
    return this.userPauseActive;
  }

  showDetecMovedOverlay(message: string) {
    this.detectMovedMessageElm = document.createElement('div');
    this.detectMovedMessageElm.style.position = 'fixed';
    this.detectMovedMessageElm.style.top = '50%';
    this.detectMovedMessageElm.style.left = '50%';
    this.detectMovedMessageElm.style.transform = 'translate(-50%, -50%)';
    this.detectMovedMessageElm.style.background = 'rgba(56, 108, 248, 0.95)';
    this.detectMovedMessageElm.style.color = '#ffffffff';
    this.detectMovedMessageElm.style.padding = '16px 24px';
    this.detectMovedMessageElm.style.borderRadius = '20px';
    this.detectMovedMessageElm.style.maxWidth = '520px';
    this.detectMovedMessageElm.style.fontSize = '18px';
    this.detectMovedMessageElm.style.lineHeight = '1.4';
    this.detectMovedMessageElm.style.fontFamily = 'Poppins, Helvetica, sans-serif';
    this.detectMovedMessageElm.style.textAlign = 'center';
    this.detectMovedMessageElm.setAttribute('role', 'status');
    this.detectMovedMessageElm.setAttribute('aria-live', 'polite');

    this.detectMovedMessageElm.textContent = message;
    document.body.appendChild(this.detectMovedMessageElm);
  }

  private showPauseOverlay(message: string) {
    if (!this.pauseOverlayElm) {
      this.pauseOverlayElm = document.createElement('div');
      this.pauseOverlayElm.id = 'gaze-pause-overlay';
      this.pauseOverlayElm.style.position = 'fixed';
      this.pauseOverlayElm.style.top = '0';
      this.pauseOverlayElm.style.left = '0';
      this.pauseOverlayElm.style.width = '100vw';
      this.pauseOverlayElm.style.height = '100vh';
  this.pauseOverlayElm.style.display = 'none';
      this.pauseOverlayElm.style.alignItems = 'center';
      this.pauseOverlayElm.style.justifyContent = 'center';
      this.pauseOverlayElm.style.background = 'rgba(15, 23, 42, 0.25)';
      this.pauseOverlayElm.style.backdropFilter = 'blur(2px)';
      this.pauseOverlayElm.style.zIndex = '900';
      this.pauseOverlayElm.style.pointerEvents = 'none';
      this.pauseOverlayElm.style.opacity = '0';
      this.pauseOverlayElm.style.transition = 'opacity 180ms ease';

      this.pauseOverlayMessageElm = document.createElement('div');
      this.pauseOverlayMessageElm.style.background = 'rgba(56, 108, 248, 0.95)';
      this.pauseOverlayMessageElm.style.color = '#ffffffff';
      this.pauseOverlayMessageElm.style.padding = '16px 24px';
      this.pauseOverlayMessageElm.style.borderRadius = '20px';
      this.pauseOverlayMessageElm.style.maxWidth = '520px';
      this.pauseOverlayMessageElm.style.fontSize = '18px';
      this.pauseOverlayMessageElm.style.lineHeight = '1.4';
      this.pauseOverlayMessageElm.style.fontFamily = 'Poppins, Helvetica, sans-serif';
      this.pauseOverlayMessageElm.style.textAlign = 'center';
      this.pauseOverlayMessageElm.setAttribute('role', 'status');
      this.pauseOverlayMessageElm.setAttribute('aria-live', 'polite');

      this.pauseOverlayElm.appendChild(this.pauseOverlayMessageElm);
      document.body.appendChild(this.pauseOverlayElm);
    }

    if (this.pauseOverlayMessageElm) {
      this.pauseOverlayMessageElm.textContent = message;
    }

    if (this.pauseOverlayElm) {
      this.pauseOverlayElm.style.pointerEvents = 'none';
      this.pauseOverlayElm.style.opacity = '1';
      this.pauseOverlayElm.style.display = 'flex';
    }
  }

  private showCalibrationOverlay(message: string) {
    if (!this.detectMovedMessage) {
      this.detectMovedMessage = document.createElement('div');
      this.detectMovedMessage.id = 'gaze-calibration-overlay';
      this.detectMovedMessage.style.position = 'fixed';
      this.detectMovedMessage.style.top = '0';
      this.detectMovedMessage.style.left = '0';
      this.detectMovedMessage.style.width = '100vw';
      this.detectMovedMessage.style.height = '100vh';
      this.detectMovedMessage.style.display = 'none';
      this.detectMovedMessage.style.alignItems = 'center';
      this.detectMovedMessage.style.justifyContent = 'center';
      this.detectMovedMessage.style.background = 'rgba(15, 23, 42, 0.25)';
      this.detectMovedMessage.style.backdropFilter = 'blur(2px)';
      this.detectMovedMessage.style.zIndex = '900';
      this.detectMovedMessage.style.pointerEvents = 'auto';
      this.detectMovedMessage.style.opacity = '0';
      this.detectMovedMessage.style.transition = 'opacity 180ms ease';

      this.detectMovedMessageElm = document.createElement('div');
      this.detectMovedMessageElm.style.background = 'rgba(56, 108, 248, 0.95)';
      this.detectMovedMessageElm.style.color = '#ffffffff';
      this.detectMovedMessageElm.style.padding = '24px 32px';
      this.detectMovedMessageElm.style.borderRadius = '20px';
      this.detectMovedMessageElm.style.maxWidth = '520px';
      this.detectMovedMessageElm.style.fontSize = '18px';
      this.detectMovedMessageElm.style.lineHeight = '1.4';
      this.detectMovedMessageElm.style.fontFamily = 'Poppins, Helvetica, sans-serif';
      this.detectMovedMessageElm.style.textAlign = 'center';
      this.detectMovedMessageElm.setAttribute('role', 'status');
      this.detectMovedMessageElm.setAttribute('aria-live', 'polite');

      this.detectMovedMessage.appendChild(this.detectMovedMessageElm);
      document.body.appendChild(this.detectMovedMessage);
    }

    if (this.detectMovedMessageElm) {
      this.detectMovedMessageElm.textContent = message;
    }

    if (this.detectMovedMessage) {
      this.detectMovedMessage.style.pointerEvents = 'auto';
      this.detectMovedMessage.style.opacity = '1';
      this.detectMovedMessage.style.display = 'flex';
    }
  }

  private hidePauseOverlay() {
    if (this.pauseOverlayElm) {
      this.pauseOverlayElm.style.opacity = '0';
      this.pauseOverlayElm.style.display = 'none';
    }
  }

  private hideCalibrationOverlay() {
    if (this.detectMovedMessage) {
      this.detectMovedMessage.style.opacity = '0';
      this.detectMovedMessage.style.display = 'none';
    }
  }

  // --- Gaze handling ---
  private isGazeFeedback(ev: MappingsOutput) {
    return (
      ev.type === MappingsOutputType.feedbackPositionH ||
      ev.type === MappingsOutputType.feedbackPositionV
    );
  }

  onGaze(ev: { x: number; y: number; raw?: unknown }) {
    // Only update the last known normalized gaze point; the RAF loop handles timing/animation
    this.gaze01.x = Math.max(0, Math.min(1, (ev.x + 1) / 2));
    this.gaze01.y = Math.max(0, Math.min(1, (ev.y + 1) / 2));
    // Mark that we have a valid gaze point (prevents auto navigation before calibration/presence)
    if (Number.isFinite(this.gaze01.x) && Number.isFinite(this.gaze01.y)) {
      this.hasValidGaze = true;
      this.lastGazeTimestamp = performance.now();
      this.gaze01Subject.next({ x01: this.gaze01.x, y01: this.gaze01.y, valid: true });

    }
  }


  private pickNavigableElementAt(x: number, y: number): Element | null {
    if (!this.hasNavigableElementsOnPage) {
      return null;
    }
    // If point is within viewport, try a direct hit first
    if (x >= 0 && y >= 0 && x <= window.innerWidth && y <= window.innerHeight) {
      const elements = document.elementsFromPoint(x, y);
      const navHit = elements.find((elm) => (elm as any).dataset?.hasOwnProperty('gridCol'));
      if (navHit) return navHit as Element;

      // Check if toolbar is being gazed at
      if (this.toolbarElement && elements.includes(this.toolbarElement)) {
        return this.toolbarElement;
      }
    }

    // Fallback: pick the nearest visible navigable element by distance to its bounding rect
    // Use runtime stickiness derived from speed profile
    const stickinessPx = this.runtimeStickinessPx;

    // If we already have a current element and we're still "close enough" to it, keep it
    if (this.currentElm) {
      const rectCur = this.currentElm.getBoundingClientRect();
      if (this.isRectRenderable(rectCur) && this.isRectInViewport(rectCur)) {
        const dCur = this.pointToRectDistance(x, y, rectCur);
        if (dCur <= stickinessPx) {
          return this.currentElm;
        }
      }
    }

    let nearest: { elm: Element; dist: number } | null = null;
    const candidates = Array.from(document.querySelectorAll('[data-grid-col]')) as Element[];
    for (const elm of candidates) {
      // Skip elements that are not displayed or have no box on screen
      const rect = elm.getBoundingClientRect();
      if (!this.isRectRenderable(rect)) continue;
      if (!this.isRectInViewport(rect)) continue;
      const dist = this.pointToRectDistance(x, y, rect);
      if (nearest === null || dist < nearest.dist) {
        nearest = { elm, dist };
      }
    }

    // Also check distance to toolbar
    if (this.toolbarElement) {
      const toolbarRect = this.toolbarElement.getBoundingClientRect();
      if (this.isRectRenderable(toolbarRect) && this.isRectInViewport(toolbarRect)) {
        const toolbarDist = this.pointToRectDistance(x, y, toolbarRect);
        if (nearest === null || toolbarDist < nearest.dist) {
          nearest = { elm: this.toolbarElement, dist: toolbarDist };
        }
      }
    }

    return nearest?.elm ?? null;
  }

  // Distance from a point to an axis-aligned rectangle (0 if inside)
  private pointToRectDistance(px: number, py: number, r: DOMRect): number {
    const dx = Math.max(r.left - px, 0, px - r.right);
    const dy = Math.max(r.top - py, 0, py - r.bottom);
    return Math.hypot(dx, dy);
  }

  private isRectRenderable(r: DOMRect): boolean {
    return Number.isFinite(r.width) && Number.isFinite(r.height) && r.width > 0 && r.height > 0;
  }

  private isRectInViewport(r: DOMRect): boolean {
    // Consider elements intersecting the viewport
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    return r.right > 0 && r.bottom > 0 && r.left < vw && r.top < vh;
  }

  // --- Dwell logic ---
  private tickDwellTimers(hovered: Element | null, dtMs: number) {
    const dwellTime = this.runtimeDwellMs ?? 1500;

    // Use runtime leak rate derived from speed profile
    const leakRate = this.runtimeLeakRate;

    // Update hovered first (increase)
    if (hovered) {
      const isToggleElm = !!this.userPauseToggleElm && hovered === this.userPauseToggleElm;
      if (this.disarmedUntilExit.has(hovered)) {
        // Keep it from re-activating until gaze leaves
        this.dwellMsByElm.set(hovered, 0);
        this.currentElm = hovered;
      } else {
        const cur = this.dwellMsByElm.get(hovered) ?? 0;
        const inc = dtMs; // increase 1:1 with time
        const next = Math.min(dwellTime, cur + inc);
        this.dwellMsByElm.set(hovered, next);
        this.currentElm = hovered;

        // Special handling for toolbar - pause the system instead of clicking
        if (hovered === this.toolbarElement && next >= dwellTime) {
          this.pauseGazeSystem();
          this.dwellMsByElm.set(hovered, 0);
          this.disarmedUntilExit.add(hovered);
        } else if (hovered !== this.toolbarElement && next >= dwellTime) {
          if (!this.userPauseActive || isToggleElm) {
            // Trigger click and reset timer for this element; disarm until gaze leaves
            try { (hovered as any).click?.(); } catch { }
            this.disarmedUntilExit.add(hovered);
          }
          this.dwellMsByElm.set(hovered, 0);
        }
      }
    }

    // Apply leak to all other tracked elements
    if (this.dwellMsByElm.size > 0) {
      const leakMs = leakRate * dtMs; // if leakRate=1, drains at same speed as fill
      for (const [elm, ms] of Array.from(this.dwellMsByElm.entries())) {
        if (elm !== hovered) {
          const next = Math.max(0, ms - leakMs);
          if (next === 0) {
            this.dwellMsByElm.delete(elm);
          } else {
            this.dwellMsByElm.set(elm, next);
          }
        }
      }
    }
  }

  private getPauseOverlayMessage(): string {
    return this.userPauseActive
      ? this.lang.words?.gazeCursor?.pauseResumeTargetsOrToggle
        ?? 'Gaze cursor paused. Look at the blue corner targets or activate the pause button again to resume control.'
      : this.lang.words?.gazeCursor?.pauseResumeTargets
        ?? 'Gaze cursor paused. Look at the blue corner targets to resume control.';
  }



  private pauseGazeSystem() {
    if (this.isPaused) {
      this.startResumeSequence();
      this.showPauseOverlay(this.getPauseOverlayMessage());
      return;
    }

    this.isPaused = true;
    // Clear all dwell timers and highlights
    this.dwellMsByElm.clear();
    this.clearHighlights();
    this.currentElm = null;
    this.lastHoverElm = null;
    this.disarmedUntilExit.clear();
    // Hide dwell visuals
    if (this.dwellElm) {
      this.dwellElm.style.opacity = '0';
    }
    if (this.ringElm) {
      this.ringElm.style.opacity = '0';
    }
    // Start resume sequence
    this.startResumeSequence();
    this.showPauseOverlay(this.getPauseOverlayMessage());
  }

  private startResumeSequence() {
    this.resumeSequenceIndex = 0;
    if (!this.hasNavigableElementsOnPage) {
      this.resumeTargets.forEach((target) => target.remove());
      this.resumeTargets = [];
      this.clearResumeTargetTimeout();
      return;
    }
    this.createResumeTargets();
    this.showNextResumeTarget();
  }

  private createResumeTargets() {
    // Clear any existing targets
    this.resumeTargets.forEach(target => target.remove());
    this.resumeTargets = [];

    // Create 4 targets (alternating between top-left and bottom-right)
    for (let i = 0; i < this.RESUME_SEQUENCE_LENGTH; i++) {
      const target = document.createElement('div');
      target.style.position = 'fixed';
      target.style.width = '35vmin';
      target.style.height = '35vmin';
      target.style.borderRadius = '50%';
      target.style.backgroundColor = 'rgba(56, 108, 248, 0.8)';
      target.style.border = '20px solid rgba(56, 108, 248, 1)';
      target.style.zIndex = '1000';
      target.style.pointerEvents = 'none';
      target.style.display = 'none'; // Hidden initially
      target.style.alignItems = 'center';
      target.style.justifyContent = 'center';
      target.style.fontSize = '12vmin';
      target.style.fontWeight = '600';
      target.style.fontFamily = 'Poppins, Helvetica, sans-serif';
      target.style.color = '#ffffffff';
      target.textContent = `${i + 1}`;

      // Position: alternate between top-left and bottom-right
      if (i % 2 === 0) {
        // Top-left
        target.style.top = '10vmin';
        target.style.left = '10vmin';
      } else {
        // Top-right
        target.style.top = '10vmin';
        target.style.right = '10vmin';
      }

      document.body.appendChild(target);
      this.resumeTargets.push(target);
    }
  }

  private showNextResumeTarget() {
    if (this.resumeSequenceIndex >= this.RESUME_SEQUENCE_LENGTH) {
      // Sequence completed - resume gaze system
      this.resumeGazeSystem();
      return;
    }

    // Hide all targets
    this.resumeTargets.forEach(target => target.style.display = 'none');

    // Show current target
    const currentTarget = this.resumeTargets[this.resumeSequenceIndex];
    if (currentTarget) {
  currentTarget.style.display = 'flex';

      // Set timeout for this target
      this.resumeTargetTimeout = window.setTimeout(() => {
        // Timeout - reset sequence
        this.resetResumeSequence();
      }, this.RESUME_TARGET_TIMEOUT_MS);
    }
  }

  private resetResumeSequence() {
    this.resumeSequenceIndex = 0;
    this.clearResumeTargetTimeout();
    this.showNextResumeTarget();
  }

  private clearResumeTargetTimeout() {
    if (this.resumeTargetTimeout) {
      clearTimeout(this.resumeTargetTimeout);
      this.resumeTargetTimeout = null;
    }
  }

  private resumeGazeSystem() {
    this.isPaused = false;
    this.userPauseActive = false;
    // Hide all resume targets
    this.resumeTargets.forEach(target => target.remove());
    this.resumeTargets = [];
    this.clearResumeTargetTimeout();
    // Re-enable normal gaze operation
    this.hidePauseOverlay();
  }

  private handleResumeSequence(x: number, y: number, dt: number) {
    if (this.resumeSequenceIndex >= this.RESUME_SEQUENCE_LENGTH) {
      return; // Sequence already completed
    }

    const currentTarget = this.resumeTargets[this.resumeSequenceIndex];
    if (!currentTarget || currentTarget.style.display === 'none') {
      return;
    }

    // Check if gaze is on the current target
    const rect = currentTarget.getBoundingClientRect();
    if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
      // Gaze is on target - advance sequence
      this.clearResumeTargetTimeout();
      this.resumeSequenceIndex++;
      this.showNextResumeTarget();
    }
  }

  // --- Visuals ---
  private startAnimationLoop() {
    if (this.rafId) return;
    const loop = () => {
      const now = performance.now();
      const dt = Math.max(0, now - this.lastTs);
      this.lastTs = now;
      const x = this.gaze01.x * window.innerWidth;
      const y = this.gaze01.y * window.innerHeight;
      if (this.hasValidGaze && (now - this.lastGazeTimestamp) > this.GAZE_TIMEOUT_MS) {
  this.hasValidGaze = false;
  this.gaze01Subject.next({ x01: this.gaze01.x, y01: this.gaze01.y, valid: false });
}


      if (this.isPaused) {
        // Handle resume sequence
        this.handleResumeSequence(x, y, dt);
      } else {
        // Normal gaze operation
        // If we haven't received any valid gaze yet, do not pick/hover or dwell
        const hovered = this.hasValidGaze ? this.pickNavigableElementAt(x, y) : null;
        if (hovered !== this.lastHoverElm) {
          if (this.lastHoverElm) this.disarmedUntilExit.delete(this.lastHoverElm);
          this.lastHoverElm = hovered;
        }
        // Gate dwell timers until we have a valid gaze point
        if (this.hasValidGaze) {
          this.tickDwellTimers(hovered, dt);
        }
        this.updateVisuals(hovered, x, y);
      }

      this.rafId = requestAnimationFrame(loop);
    };
    this.rafId = requestAnimationFrame(loop);
  }

  /**
   * Update the full speed profile from a single dwell time value.
   * Persists dwell time and derives leak rate, stickiness, and transition speed.
   * This is the primary method called by the speed adjustment modal.
   */
  updateSpeedProfile(dwellMs: number) {
    this.dwell.set(dwellMs);
    this.runtimeDwellMs = dwellMs;
    this.applySpeedProfileFromDwell(dwellMs);
    document.documentElement.style.setProperty('--dwell-time', `${dwellMs}ms`);
    console.log(
      `[GazeCursor] Speed profile updated: dwell=${dwellMs}ms, ` +
      `leak=${this.runtimeLeakRate.toFixed(2)}, ` +
      `stickiness=${this.runtimeStickinessPx.toFixed(0)}px, ` +
      `transition=${this.runtimeTransitionMs.toFixed(0)}ms`
    );
  }

  /**
   * Update the dwell time and persist it via DwellTimeService.
   * Delegates to updateSpeedProfile() so all derived params stay in sync.
   */
  updateDwellTime(ms: number) {
    this.updateSpeedProfile(ms);
  }

  /** Returns the current runtime dwell time in milliseconds. */
  getDwellTime(): number {
    return this.runtimeDwellMs;
  }


  private createDwellElm() {
    if (!this.dwellElm) {
      const ms = Math.round(this.runtimeTransitionMs);
      const ease = 'cubic-bezier(0.2,0.8,0.2,1)';

      this.dwellElm = document.createElement('div');
      this.dwellElm.id = 'gaze-dwell-overlay';
      // Use fixed so top/left are in viewport coordinates (match getBoundingClientRect)
      this.dwellElm.style.position = 'fixed';
      // Start centered in the viewport to avoid a brief top-left flash
      this.dwellElm.style.top = '50%';
      this.dwellElm.style.left = '50%';
      this.dwellElm.style.transform = 'translate(-50%, -50%)';
      this.dwellElm.style.pointerEvents = 'none';
      // Start hidden by default
      this.dwellElm.style.opacity = '0';
      this.dwellElm.style.boxSizing = 'border-box';
      this.dwellElm.style.transition =
        `top ${ms}ms ${ease}, left ${ms}ms ${ease}, width ${ms}ms ${ease}, height ${ms}ms ${ease}, opacity 140ms linear`;
      this.dwellElm.style.willChange = 'top, left, width, height, opacity';

      this.ringElm = document.createElement('div');
      this.ringElm.id = 'gaze-dwell-ring';
      this.ringElm.style.position = 'absolute';
      this.ringElm.style.top = '0px';
      this.ringElm.style.left = '0px';
      this.ringElm.style.width = '0px';
      this.ringElm.style.height = '0px';
      this.ringElm.style.opacity = '0.12';
      this.ringElm.style.boxSizing = 'border-box';
      // Keep the shrinking ring visible (it provides the dwell animation)
      this.ringElm.style.border = '30px solid rgba(56,108,248,0.25)';
      this.ringElm.style.background = 'transparent';
      this.ringElm.style.filter = 'none';
      this.ringElm.style.transformOrigin = 'center center';
      // Animate the ring's layout changes as well for a smooth shrink/slide.
      this.ringElm.style.transition =
        `top ${ms}ms ${ease}, left ${ms}ms ${ease}, width ${ms}ms ${ease}, height ${ms}ms ${ease}, border-radius ${ms}ms ${ease}, opacity 140ms linear`;
      this.ringElm.style.willChange = 'top, left, width, height, borderRadius, opacity';
      this.ringElm.style.pointerEvents = 'none';

      this.dwellElm.appendChild(this.ringElm);
      document.body.appendChild(this.dwellElm);

      this.gazeDotElm = document.createElement('div');
      this.gazeDotElm.style.position = 'absolute';
      this.gazeDotElm.style.width = '100px'; // initial
      this.gazeDotElm.style.height = '100px';
      this.gazeDotElm.style.borderRadius = '50%';
      this.gazeDotElm.style.backgroundColor = 'transparent';
      this.gazeDotElm.style.border = '2px solid rgba(56,108,248, 0.1)';
      this.gazeDotElm.style.opacity = '1';
      this.gazeDotElm.style.pointerEvents = 'none';
      this.gazeDotElm.style.zIndex = '100';
      document.body.appendChild(this.gazeDotElm);
    }
  }

  private teardownVisuals() {
    // Remove highlight from any elements still marked
    this.clearHighlights();
    this.dwellElm?.remove();
    this.dwellElm = null;
    this.ringElm = null;
    this.gazeDotElm?.remove();
    this.gazeDotElm = null;
    if (this.dwellHideTimer !== null) {
      clearTimeout(this.dwellHideTimer);
      this.dwellHideTimer = null;
    }
  }

  private updateVisuals(hovered: Element | null, gazeX?: number, gazeY?: number) {
    // Don't show visuals when paused
    if (this.isPaused) {
      if (this.dwellHideTimer !== null) {
        clearTimeout(this.dwellHideTimer);
        this.dwellHideTimer = null;
      }
      this.clearHighlights();
      if (this.dwellElm) {
        this.dwellElm.style.opacity = '0';
      }
      if (this.ringElm) {
        this.ringElm.style.opacity = '0';
      }
      return;
    }

    if (!this.hasNavigableElementsOnPage) {
      this.performDwellHide();
      if (this.gazeDotElm) {
        this.gazeDotElm.style.display = 'none';
      }
      return;
    }

    // Highlight item and update progress bar size
    if (!this.dwellElm) return;

    const suppressHighlight = this.userPauseActive && (!hovered || hovered !== this.userPauseToggleElm);
    const effectiveHovered = suppressHighlight ? null : hovered;

    // Update hide-border preference at runtime so toggling the setting takes effect immediately
    const hideBorderNow = this.profile.navSettingsBundle?.bundleOptionsState?.gazeHideDwellBorder ?? this.hideDwellBorder;

    // Always clear any stale highlights (keep the currently hovered element, if any)
    // This prevents leftover highlights when moving quickly across many elements.
    this.clearHighlights(effectiveHovered ?? undefined);

    this.currentElm = effectiveHovered ?? null;

    // Until we get a valid gaze, do not show highlights or ring/dwell progress
    if (effectiveHovered && this.hasValidGaze) {
      if (this.dwellHideTimer !== null) {
        clearTimeout(this.dwellHideTimer);
        this.dwellHideTimer = null;
      }
      // Re-apply highlight to the current hovered element
      effectiveHovered.classList.add('app-cursor-highlight');
      const rect = effectiveHovered.getBoundingClientRect();
      const dwellTime = this.runtimeDwellMs ?? 1500;

      const curMs = this.dwellMsByElm.get(effectiveHovered) ?? 0;
      const progress = Math.max(0, Math.min(1, curMs / dwellTime));

      // Round layout values to whole pixels to avoid subpixel jitter during transitions
      const top = Math.round(rect.top);
      const left = Math.round(rect.left);
      const width = Math.round(rect.width);
      const height = Math.round(rect.height);

      // Position container aligned with element (no size dependence here)
      this.dwellElm.style.top = `${top}px`;
      this.dwellElm.style.left = `${left}px`;
      this.dwellElm.style.width = `${width}px`;
      this.dwellElm.style.height = `${height}px`;
      this.dwellElm.style.opacity = '1';
      // Clear initial centering transform so the overlay aligns with the element's top-left corner
      this.dwellElm.style.transform = 'none';

      // Ring should coincide with the element's border initially and shrink towards center
      const inset = hideBorderNow ? 6 : 0;
      const ringW = Math.max(4, width - inset * 2);
      const ringH = Math.max(4, height - inset * 2);
      const scale = 1 - progress; // 1 at start, 0 at end => shrinks towards center

      // Match element border radius, scaling values per-corner if present
  const br = window.getComputedStyle(effectiveHovered).borderRadius || '0px';

      if (this.ringElm) {
        // Compute scaled dimensions and position and apply them. Because we
        // added transitions on the ring element, these changes will animate
        // smoothly and appear to slide/scale behind the grid elements.
        const scaledW = Math.round(ringW * scale);
        const scaledH = Math.round(ringH * scale);
        const scaledBr = br.replace(/([0-9]*\.?[0-9]+)px/g, (_m, n) => `${parseFloat(n) * scale}px`);
        this.ringElm.style.top = `${Math.round(inset + (ringH - scaledH) / 2)}px`;
        this.ringElm.style.left = `${Math.round(inset + (ringW - scaledW) / 2)}px`;
        this.ringElm.style.width = `${scaledW}px`;
        this.ringElm.style.height = `${scaledH}px`;
        this.ringElm.style.borderRadius = scaledBr;
        this.ringElm.style.opacity = '1';
      }
    } else {
      // No hovered element: ensure no stale highlights remain
      this.scheduleDwellHide(!this.hasValidGaze);
    }

    // Update gaze point dot
    if (this.viewGazePoint && this.hasValidGaze && gazeX !== undefined && gazeY !== undefined && this.gazeDotElm) {
      const diameter = 0.2 * window.innerHeight;
      this.gazeDotElm.style.width = `${diameter}px`;
      this.gazeDotElm.style.height = `${diameter}px`;
      this.gazeDotElm.style.left = `${gazeX - diameter / 2}px`;
      this.gazeDotElm.style.top = `${gazeY - diameter / 2}px`;
      this.gazeDotElm.style.display = 'block';
    } else if (this.gazeDotElm) {
      this.gazeDotElm.style.display = 'none';
    }
  }

  private scheduleDwellHide(immediate = false) {
    if (this.dwellHideTimer !== null) {
      if (!immediate) {
        return;
      }
      clearTimeout(this.dwellHideTimer);
      this.dwellHideTimer = null;
    }

    if (immediate) {
      this.performDwellHide();
      return;
    }

    this.dwellHideTimer = window.setTimeout(() => {
      this.dwellHideTimer = null;
      this.performDwellHide();
    }, 120);
  }

  private performDwellHide() {
    this.clearHighlights();
    if (this.dwellElm) {
      this.dwellElm.style.opacity = '0';
    }
    if (this.ringElm) {
      this.ringElm.style.opacity = '0';
    }
  }
}